import React, { useState, useEffect } from 'react';
import PageTabs from '../shared/PageTabs';
import { SETTINGS_TABS } from '../shared/tabConfigs';
import { Box, Typography, Tabs, Tab, Divider } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { settingsService } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { useUser } from '../../context/UserContext';
import { tokens } from '../../theme';
// Real existing components mounted as Settings tabs (Feedback, Email Templates).
// Both are zero-prop default exports.
import FeedbackSettings from './FeedbackSettings';
import EmailTemplates from './EmailTemplates';
import EmbedSettings from './EmbedSettings';

const T = tokens;

// ── Tab panel wrapper ─────────────────────────────────────────────────────────
const TabPanel = ({ children, value, index }) => (
  <Box sx={{ display: value === index ? 'block' : 'none', paddingTop: '24px' }}>
    {children}
  </Box>
);

// ── Section header ────────────────────────────────────────────────────────────
const SectionHeader = ({ title, subtitle }) => (
  <Box sx={{ marginBottom: '20px' }}>
    <Typography sx={{ fontSize: T.font.size.lg, fontWeight: T.font.weight.medium, color: T.color.textPrimary, fontFamily: T.font.family, marginBottom: '4px' }}>
      {title}
    </Typography>
    {subtitle && (
      <Typography sx={{ fontSize: T.font.size.md, color: T.color.textSecondary, fontFamily: T.font.family }}>
        {subtitle}
      </Typography>
    )}
  </Box>
);

// ── Form field ────────────────────────────────────────────────────────────────
const FormField = ({ label, hint, children }) => (
  <Box sx={{ marginBottom: '20px' }}>
    <Typography sx={{ fontSize: T.font.size.sm, fontWeight: T.font.weight.medium, color: T.color.textPrimary, fontFamily: T.font.family, marginBottom: '6px' }}>
      {label}
    </Typography>
    {hint && (
      <Typography sx={{ fontSize: T.font.size.xs, color: T.color.textTertiary, fontFamily: T.font.family, marginBottom: '8px' }}>
        {hint}
      </Typography>
    )}
    {children}
  </Box>
);

// ── Input ─────────────────────────────────────────────────────────────────────
const Input = ({ value, onChange, placeholder, disabled, type = 'text', maxLength }) => (
  <Box
    component="input"
    type={type}
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    disabled={disabled}
    maxLength={maxLength}
    sx={{
      width: '100%', maxWidth: '480px',
      fontSize: T.font.size.md, color: disabled ? T.color.textTertiary : T.color.textPrimary,
      background: disabled ? T.color.bgSecondary : T.color.bgPrimary,
      border: `0.5px solid ${T.color.borderSecondary}`,
      borderRadius: T.radius.sm, padding: '8px 12px',
      fontFamily: T.font.family, outline: 'none',
      '&:focus': { borderColor: T.color.borderPrimary },
      '&:disabled': { cursor: 'not-allowed' },
    }}
  />
);

// ── Save button ───────────────────────────────────────────────────────────────
const SaveBtn = ({ onClick, loading }) => (
  <Box
    onClick={!loading ? onClick : undefined}
    sx={{
      fontSize: T.font.size.sm, fontWeight: T.font.weight.medium,
      color: T.color.bgPrimary, background: loading ? T.color.textTertiary : T.color.textPrimary,
      borderRadius: T.radius.sm, padding: '7px 20px',
      cursor: loading ? 'default' : 'pointer', fontFamily: T.font.family,
      display: 'inline-block', transition: `background ${T.transition}`,
      '&:hover': { background: loading ? T.color.textTertiary : '#2A2A28' },
    }}
  >
    {loading ? 'Saving…' : 'Save changes'}
  </Box>
);

// Removed 23 Oct 2026 (K12 pt 11, agreed 23 Oct): the embed snippet's code block (CodeBlock), its state (embedCode, copied, the
// workspace slug, signup and portal addresses) and its copy handlers (handleCopyEmbed, handleCopy). The General tab never
// showed them; the Embed tab is EmbedSettings.

// ── Toggle ────────────────────────────────────────────────────────────────────
const Toggle = ({ checked, onChange, label, hint }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '16px', maxWidth: '480px' }}>
    <Box>
      <Typography sx={{ fontSize: T.font.size.md, color: T.color.textPrimary, fontFamily: T.font.family }}>{label}</Typography>
      {hint && <Typography sx={{ fontSize: T.font.size.sm, color: T.color.textSecondary, fontFamily: T.font.family, marginTop: '2px' }}>{hint}</Typography>}
    </Box>
    <Box
      onClick={() => onChange(!checked)}
      sx={{
        width: '40px', height: '22px', borderRadius: '11px',
        background: checked ? T.color.textPrimary : T.color.borderSecondary,
        position: 'relative', cursor: 'pointer', flexShrink: 0, marginLeft: '16px',
        transition: `background ${T.transition}`,
      }}
    >
      <Box sx={{
        position: 'absolute', top: '3px',
        left: checked ? '21px' : '3px',
        width: '16px', height: '16px', borderRadius: '50%',
        background: '#fff', transition: `left ${T.transition}`,
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      }} />
    </Box>
  </Box>
);

// ── Main component ────────────────────────────────────────────────────────────
const GeneralSettings = () => {
  const { showToast }           = useToast();
  const { updateUserProfile }   = useUser();
  const [searchParams]          = useSearchParams();
  const initialTab              = searchParams.get('tab') === 'embed' ? 3 : 0; // General default; ?tab=embed -> Embed

  const [tab,            setTab]            = useState(initialTab);
  const [saving,         setSaving]         = useState(false);

  // General settings state
  const [workspaceName,  setWorkspaceName]  = useState('');
  const [primaryDomain,  setPrimaryDomain]  = useState('');
  const [logo,           setLogo]           = useState(null);
  const [logoPreview,    setLogoPreview]    = useState(null);
  const [selfSignup,     setSelfSignup]     = useState(false);
  const [userCap,        setUserCap]        = useState('');  
  // Include admin and author activity in insights (N46 pt 5), off by default.
  const [includeStaff,   setIncludeStaff]   = useState(false);
  const [errors,         setErrors]         = useState({});
  const [currentSelfSignups, setCurrentSelfSignups] = useState(0);
  // The cap as saved, which the sign-up steps enforce, for the count line (N47 pt 5).
  const [savedCap, setSavedCap] = useState(0);
  // Removed 2 Nov 2026 (N47 pt 2): maxUserCap, twice the workspace's trial user limit, the most the cap could be. The
  // cap is the customer's own and not tied to the plan's allowance; with no trial limit set it was 0, so self-signup
  // could not be turned on at all.

  // From the saved cap, not the field as it is being typed (N47 pt 5). Removed 2 Nov 2026 (N47 pt 5): working the line
  // out from the field, so it changed with each key and showed a cap that wasn't saved.
  const remaining = Math.max(0, savedCap - currentSelfSignups);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await settingsService.getGeneralSettings();
        if (res.data.success) {
          const s = res.data.settings;
          setCurrentSelfSignups(s.self_signup_user_count || 0);
          setWorkspaceName(s.workspace_name || '');
          setPrimaryDomain(s.primary_domain || '');
          setLogoPreview(s.logo_url || null);
          setSelfSignup(s.self_signup_enabled !== false);
          setUserCap(s.user_cap || '');
          setSavedCap(Number(s.user_cap) || 0);
          setIncludeStaff(s.include_staff_activity === true);
        }
      } catch (err) {
        console.error('Error fetching settings:', err);
      }
    };
    fetchSettings();
  }, []);  

  const handleSaveGeneral = async () => {
    if (!validate()) {
      return;
    }
    setSaving(true);
    try {
      await settingsService.updateGeneralSettings({
        workspace_name:      workspaceName,
        self_signup_enabled: selfSignup,
        user_cap:            userCap || null,
        include_staff_activity: includeStaff,
      });
      // The new name shows in this admin's sidebar at once; everyone else's at their next page (N47 pts 6, 6.1).
      updateUserProfile({ workspace_name: workspaceName.trim() });
      setSavedCap(Number(userCap) || 0);
      showToast('Settings saved', 'success');
    } catch (err) {
      showToast('Failed to save settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const validate = () => {
    const newErrors = {};

    // The workspace name is saved (N47 pt 6), so it can't be left empty.
    if (!workspaceName.trim()) {
      newErrors.workspaceName = 'Workspace name is required';
    }

    // Self-signup on needs a cap of at least 1, a whole number, as the server requires (N47 pt 2). Removed 2 Nov 2026
    // (N47 pt 2): 'User cap must be greater than 0.' (a whole number now) and 'User cap cannot exceed N.'
    if (selfSignup) {
      const capText = String(userCap ?? '').trim();

      if (!capText) {
        newErrors.userCap = 'User cap is required when self-signup is enabled.';
      } else if (!/^\d+$/.test(capText) || Number(capText) < 1) {
        newErrors.userCap = 'User cap must be a whole number greater than 0.';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const preview = URL.createObjectURL(file);
    setLogoPreview(preview);
    try {
      const formData = new FormData();
      formData.append('logo', file);
      await settingsService.uploadLogo(formData);
      showToast('Logo updated', 'success');
    } catch (err) {
      showToast('Failed to upload logo', 'error');
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <PageTabs tabs={SETTINGS_TABS} />
      {/* Page header */}
      <Typography variant="h4" sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        Settings
      </Typography>    

      {/* ── General tab ───────────────────────────────────────────────────── */}
      <TabPanel value={tab} index={0}>
        <Box sx={{ maxWidth: '560px' }}>

          <SectionHeader title="Workspace" subtitle="Basic information about your workspace." />

          <FormField label="Workspace name">
            <Input value={workspaceName} onChange={e => setWorkspaceName(e.target.value)} placeholder="Acme Corp" maxLength={255} />
            {errors.workspaceName && (
              <Typography color="error" variant="caption" display="block" sx={{ mt: 1 }}>
                {errors.workspaceName}
              </Typography>
            )}
          </FormField>

          {/* In-app appearance — live preview of the sidebar workspace mark
              (terracotta rounded square + name on the dark rail). This is what
              learners see in the app; the uploaded logo below is used on
              sign-in pages and emails instead. */}
          <FormField label="In-app appearance" hint="How your workspace appears in the sidebar for everyone in this workspace.">
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '10px', background: '#16161A', borderRadius: T.radius.md, padding: '10px 16px' }}>
              <Box sx={{ width: '26px', height: '26px', borderRadius: '7px', background: '#185FA5', color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 600, fontFamily: T.font.family, flexShrink: 0 }}>
                {(workspaceName.charAt(0) || 'A').toUpperCase()}
              </Box>
              <Box sx={{ fontSize: '14px', fontWeight: 500, color: '#FFFFFF', fontFamily: T.font.family, letterSpacing: '-0.01em' }}>
                {workspaceName || 'Acme Corp'}
              </Box>
            </Box>
          </FormField>

          <FormField label="Primary domain" hint="Used to classify Internal vs External users. Cannot be changed after setup.">
            <Input value={primaryDomain} disabled placeholder="acmecorp.com" />
          </FormField>

          {/* The logo is shown on the sign-in pages only, and the upload takes JPG, PNG and GIF (N47 pt 7, agreed 19 and 20
              Oct). Removed 23 Oct 2026 (N47 pt 7): the hint "Used on your sign-in page and email notifications. In the app,
              your workspace appears as the mark shown below. Recommended: 200×200px, PNG or SVG." (no email shows the logo,
              and the upload refuses SVG), and a file picker offering every image type. */}
          <FormField label="Workspace logo" hint="Shown on your sign-in pages.">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              {logoPreview ? (
                <Box sx={{ width: '60px', height: '60px', borderRadius: T.radius.md, border: `0.5px solid ${T.color.borderTertiary}`, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <img src={logoPreview} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                </Box>
              ) : (
                <Box sx={{ width: '60px', height: '60px', borderRadius: T.radius.md, border: `0.5px solid ${T.color.borderTertiary}`, background: T.color.bgSecondary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', color: T.color.textTertiary }}>
                  {workspaceName.charAt(0).toUpperCase() || 'A'}
                </Box>
              )}
              <Box>
                <Box component="label" htmlFor="logo-upload" sx={{ fontSize: T.font.size.sm, color: T.color.textPrimary, border: `0.5px solid ${T.color.borderSecondary}`, borderRadius: T.radius.sm, padding: '6px 14px', cursor: 'pointer', fontFamily: T.font.family, display: 'inline-block', '&:hover': { background: T.color.bgSecondary } }}>
                  Upload logo
                </Box>
                <input id="logo-upload" type="file" accept=".png,.jpg,.jpeg,.gif,image/png,image/jpeg,image/gif" style={{ display: 'none' }} onChange={handleLogoUpload} />
                <Typography sx={{ fontSize: T.font.size.sm, color: T.color.textSecondary, fontFamily: T.font.family, marginTop: '6px' }}>
                  PNG, JPG or GIF, 200×200px
                </Typography>
              </Box>
            </Box>
          </FormField>

          <Divider sx={{ margin: '24px 0', borderColor: T.color.borderTertiary }} />
          <SectionHeader title="Access" subtitle="Control who can sign up and how many users you allow." />

          <Toggle
            checked={selfSignup}
            onChange={setSelfSignup}
            label="Allow self-signup"
            hint="When enabled, external users can sign up at your signup URL."
          />
          {/* The self-signups so far against the saved cap (N47 pt 5). Removed 2 Nov 2026 (N47 pt 5):
              'Self-signups: N of M cap · K remaining'. */}
          {selfSignup && savedCap > 0 && (
            <Typography variant="body2" color="text.secondary">
              Self-signups: {currentSelfSignups} of {savedCap} used, {remaining} remaining.
            </Typography>
          )}

          {/* Removed 2 Nov 2026 (N47 pt 2): the hint 'Maximum allowed number of cap (1 - N).' and the placeholder 'Enter a
              value up to N', for the upper limit that went. */}
          {/* The cap's hint says that self-signups count as active learners for the month they join (N47 pt 4). */}
          {selfSignup && (
            <FormField
              label="User cap"
              hint="Each person who signs up counts as an active learner for the month they join."
              error={errors.userCap}
            >
              <Input
                value={userCap}
                onChange={(e) => setUserCap(e.target.value)}
                type="number"
              />
              {errors.userCap && (
                <Typography color="error" variant="caption" display="block" sx={{ mt: 1 }}>
                  {errors.userCap}
                </Typography>
              )}
            </FormField>
          )}

          <Divider sx={{ margin: '24px 0', borderColor: T.color.borderTertiary }} />
          <SectionHeader title="Insights" subtitle="What the search and content numbers count." />

          <Toggle
            checked={includeStaff}
            onChange={setIncludeStaff}
            label="Include admin and author activity in insights"
          />
          {/* The label alone (N46 pt 5.1, agreed 22 Oct). Removed 23 Oct 2026 (N46 pt 5.1): the hint under it, "Off:
              searches by admins and authors, who are testing the product, are left out of the search numbers, so they
              show what learners asked. Turn it on to test or demo." */}

          <Box sx={{ marginTop: '24px' }}>
            <SaveBtn onClick={handleSaveGeneral} loading={saving} />
          </Box>
        </Box>
      </TabPanel>

      {/* ── Feedback tab (real FeedbackSettings component) ─────────────────── */}
      <TabPanel value={tab} index={1}>
        <FeedbackSettings />
      </TabPanel>

      {/* ── Email Templates tab (real EmailTemplates component) ────────────── */}
      <TabPanel value={tab} index={2}>
        <EmailTemplates />
      </TabPanel>

      {/* ── Embed tab ─────────────────────────────────────────────────────── */}
      <TabPanel value={tab} index={3}>
        <EmbedSettings />
      </TabPanel>      
    </Box>
  );
};

export default GeneralSettings;
